import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination, BaseResponse, Filters, FiltersPagination,
} from '@/types';
import { Notifications } from '../types';
import { popup_notifications_limit } from '../consts';

interface NotificationsList extends BaseResponse, ApiPagination {
  data: {
    number_unread_notifications: number,
    items: Notifications.Notification[],
    sections: Notifications.Section[],
  }
}

interface UserUnreadEntitiesList extends BaseResponse {
  data: {
    items: Notifications.UserUnreadEntities[],
  }
}

interface UserNotificationsList extends BaseResponse {
  data: {
    items: Notifications.UserNotifications[],
  }
}

interface ClientSettingResponse extends BaseResponse {
  data: Notifications.ClientSetting,
}

interface ClientSettingRequest {
  type: Notifications.Types,
  period: Notifications.Periods,
  structure: Notifications.Structures,
  selection: Notifications.Selections,
  type_events: Notifications.TypeEvents,
  critical_events: Notifications.CriticalEvents,
  dispatch_to: string,
  weekdays: number[],
  mark_ids: number[],
  status_ids: number[],
  cities: string[],
  structure_event_type_ids: number[],
  critical_event_type_ids: number[],
  is_critical: boolean,
}

interface ReadEntitiesInTablePayload {
  page: string
  items: number[]
}

export class NotificationsService extends BaseApiService {
  private readonly base_path = '/notifications'

  async get_notifs(filters: FiltersPagination<Filters>): Promise<NotificationsList> {
    const payload = filters;

    const { data } = await this.http_client.get<NotificationsList>(this.base_path, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_last_notifs(without_news?: boolean): Promise<NotificationsList> {
    const params = {
      limit: 10,
      filters: {
        is_without_news: false,
        not_read_only: true,
      },
    };

    if (without_news) {
      params.filters.is_without_news = true;
    }

    const { data } = await this.http_client.get<NotificationsList>(this.base_path, {
      params,
    });

    return data;
  }

  async get_last_news(): Promise<NotificationsList> {
    const { data } = await this.http_client.get<NotificationsList>(`${this.base_path}/news`, {
      params: {
        limit: 10,
      },
    });

    return data;
  }

  async get_user_notifs(): Promise<UserNotificationsList> {
    const url = 'user/me/notifications';
    const { data } = await this.http_client.get<UserNotificationsList>(url);

    return data;
  }

  async get_unread_entities(): Promise<UserUnreadEntitiesList> {
    const url = 'user/me/unread_entities';
    const { data } = await this.http_client.get<UserUnreadEntitiesList>(url);

    return data;
  }

  async update_unread_entities(items: Notifications.UserUnreadEntities[]): Promise<NotificationsList> {
    const url = 'user/me/unread_entities';
    const payload = {
      items,
    };

    const { data } = await this.http_client.patch<NotificationsList>(url, payload);

    return data;
  }

  async save_user_notifs(payload: Notifications.UserNotifications): Promise<UserNotificationsList> {
    const url = 'user/me/notifications';
    const { data } = await this.http_client.patch<UserNotificationsList>(url, payload);

    return data;
  }

  async reading_notifs(ids: number | Array<number>, filters: FiltersPagination<Filters> | []) {
    const payload = filters;

    const { data } = await this.http_client.patch<NotificationsList>(this.base_path, {
      notification_ids: ids,
    });
  }

  async read_entities_in_table(payload: ReadEntitiesInTablePayload) {
    const url = 'user/me/unread_entities';
    const { data } = await this.http_client.post(url, payload);

    return data;
  }

  async get_popup_notifications() {
    const url = `${this.base_path}/popup`;
    const { data } = await this.http_client.get(url, {
      params: {
        limit: popup_notifications_limit,
      },
    });

    return data;
  }

  async getClientEventTypes(): Promise<Notifications.ClientEventType[]> {
    const url = `${this.base_path}/client-event-types`;
    const { data } = await this.http_client.get(url);

    return data.data;
  }

  async getClientEventChannels(): Promise<Notifications.ClientEventChannel[]> {
    const url = `${this.base_path}/client-event-channels`;
    const { data } = await this.http_client.get(url);

    return data.data;
  }

  async storeClientEventChannelBlock(request: unknown): Promise<Notifications.ClientEventChannelBlock> {
    const url = `${this.base_path}/client-event-channel-blocks`;
    const { data } = await this.http_client.post(url, request);

    return data.data;
  }

  async destroyClientEventChannelBlock(block: Notifications.ClientEventChannelBlock) {
    const url = `${this.base_path}/client-event-channel-blocks/${block.id}`;

    await this.http_client.delete(url);
  }

  async getClientSettings(): Promise<Notifications.ClientSetting> {
    const url = `${this.base_path}/client-settings`;
    const { data } = await this.http_client.get<ClientSettingResponse>(url);

    return data.data;
  }

  async storeClientSettings(request: ClientSettingRequest): Promise<void> {
    const url = `${this.base_path}/client-settings`;

    await this.http_client.post(url, request);
  }
}
